import { ROW_NUMBER_COLUMN_WIDTH } from 'style/constants'

import { IViewPortColumn } from '../types'
import { ITableViewGroup, ITableViewColumn } from 'types'

const COLUMN_BUFFER = 3 // Render extra columns on each side for smooth scrolling

const MIN_TREE_COLUMN_WIDTH = 250

interface Args {
  rawColumns: readonly ITableViewColumn[]
  rawGroupByColumns: readonly ITableViewGroup[]
  frozenIndex: number
  hiddenColumnIds: string[]
  scrollLeft: number
  viewportWidth: number
  treeGroupView: boolean
}

export function useColumnsViewport({
  rawColumns,
  rawGroupByColumns,
  frozenIndex,
  hiddenColumnIds,
  scrollLeft,
  viewportWidth,
  treeGroupView
}: Args) {
  // The compiler's infer mode skips a hook that calls no hooks, so opt in: the data provider calls
  // this on every vertical scroll frame, and a fresh allColumns each time would change the data
  // context and re-render every memoised row with it.
  'use memo'
  // Process all columns - filter hidden, sort by grouping, calculate positions
  const computeAllColumnsData = () => {
    const groupedColumnIds = rawGroupByColumns.map((group) => group.columnId)

    const columns = rawColumns
      .filter((column) => !hiddenColumnIds.includes(column.publicId)) /// remove hidden columns from view
      .sort((columnA, columnB) => {
        // Sort grouped columns
        if (groupedColumnIds.includes(columnA.publicId)) {
          if (groupedColumnIds.includes(columnB.publicId)) {
            return groupedColumnIds.indexOf(columnA.publicId) - groupedColumnIds.indexOf(columnB.publicId)
          }
          return -1
        }
        if (groupedColumnIds.includes(columnB.publicId)) return 1

        // Sort other columns last
        return 0
      })
      .map((rawColumn, idx) => {
        const isGrouped = groupedColumnIds.includes(rawColumn.publicId) ?? false
        const frozen = idx < frozenIndex
        const originalPosition = rawColumns.indexOf(rawColumn)

        const viewPortColumn: IViewPortColumn = {
          column: rawColumn,
          originalPosition,
          isFrozen: frozen,
          position: idx,
          isGrouped,
          isLastFrozenColumn: false,
          isLastGroupedColumn: false,
          left: 0 // cumulative left offset
        }

        return viewPortColumn
      })

    // Find last frozen and last grouped column indices after mapping
    let lastFrozenColumnIndex = -1
    let lastGroupedColumnIndex = -1
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].isFrozen) lastFrozenColumnIndex = i
      if (columns[i].isGrouped) lastGroupedColumnIndex = i
    }

    if (lastFrozenColumnIndex !== -1) {
      columns[lastFrozenColumnIndex].isLastFrozenColumn = true
    }
    if (lastGroupedColumnIndex !== -1) {
      columns[lastGroupedColumnIndex].isLastGroupedColumn = true
    }

    // In tree group view mode, compute the tree column width from grouped columns
    // and then filter them out so only non-grouped columns remain.
    // Also extract the first non-grouped column as the "tree leaf column" —
    // it renders as an editable cell inside the tree column area instead of as a data column.
    let computedTreeColumnWidth = 0
    let finalColumns = columns
    let extractedTreeLeafColumn: IViewPortColumn | null = null
    if (treeGroupView && groupedColumnIds.length > 0) {
      // Remove grouped columns — the tree column replaces them
      const nonGrouped = columns.filter((col) => !col.isGrouped)

      // Extract first non-grouped column to render inside the tree column area.
      // Its width drives the tree column width (resizable via standard column resize).
      if (nonGrouped.length > 0) {
        extractedTreeLeafColumn = nonGrouped[0]
        computedTreeColumnWidth = Math.max(extractedTreeLeafColumn.column.width, MIN_TREE_COLUMN_WIDTH)
        finalColumns = nonGrouped.slice(1)
      } else {
        const groupedWidth = columns.filter((col) => col.isGrouped).reduce((sum, col) => sum + col.column.width, 0)
        computedTreeColumnWidth = Math.max(groupedWidth, MIN_TREE_COLUMN_WIDTH)
        finalColumns = nonGrouped
      }

      // Re-apply frozen flags for remaining data columns.
      // The tree column counts as the first frozen column,
      // so freeze (frozenIndex - 1) additional data columns.
      const dataColumnsToFreeze = Math.max(0, frozenIndex - 1)
      finalColumns.forEach((col, idx) => {
        col.isFrozen = idx < dataColumnsToFreeze
      })
    }

    // Headers and cells get placed after the row number cell, so we add an offset here
    // In tree mode, the tree column sits between the row number and the first data column
    let leftOffset = ROW_NUMBER_COLUMN_WIDTH + computedTreeColumnWidth

    finalColumns.forEach((viewportColumn, idx) => {
      viewportColumn.position = idx
      viewportColumn.left = leftOffset
      leftOffset += viewportColumn.column.width
    })

    // Recalculate lastFrozenColumnIndex for the filtered columns
    let adjustedLastFrozenColumnIndex = -1
    finalColumns.forEach((col, idx) => {
      col.isLastFrozenColumn = false
      if (col.isFrozen) adjustedLastFrozenColumnIndex = idx
    })
    if (adjustedLastFrozenColumnIndex >= 0) {
      finalColumns[adjustedLastFrozenColumnIndex].isLastFrozenColumn = true
    }

    // Calculate frozen width for viewport calculations
    const frozenWidth =
      adjustedLastFrozenColumnIndex >= 0
        ? finalColumns[adjustedLastFrozenColumnIndex].left + finalColumns[adjustedLastFrozenColumnIndex].column.width
        : ROW_NUMBER_COLUMN_WIDTH + computedTreeColumnWidth

    return {
      allColumns: finalColumns,
      lastFrozenColumnIndex: adjustedLastFrozenColumnIndex,
      frozenWidth,
      treeColumnWidth: computedTreeColumnWidth,
      treeLeafColumn: extractedTreeLeafColumn
    }
  }
  const { allColumns, lastFrozenColumnIndex, frozenWidth, treeColumnWidth, treeLeafColumn } = computeAllColumnsData()

  // Calculate total width for scroll container sizing
  // When no data columns are visible, fall back to frozenWidth so the tree column
  // and row numbers still have enough space and the grid doesn't collapse.
  const computeTotalWidth = () => {
    if (allColumns.length === 0) return frozenWidth
    const lastCol = allColumns[allColumns.length - 1]
    return lastCol.left + lastCol.column.width
  }
  const totalWidth = computeTotalWidth()

  // Split columns into sticky (frozen or grouped) and scrollable
  // Grouped columns should always be rendered and stay sticky, just like frozen columns
  const stickyColumns = allColumns.filter((col: IViewPortColumn) => col.isFrozen)
  const scrollableColumns = allColumns.filter((col: IViewPortColumn) => !col.isFrozen)

  // Calculate which scrollable columns are visible in the viewport
  const computeVisibleColumns = () => {
    // Return all columns if no viewport width yet (initial render)
    if (viewportWidth <= 0) {
      return {
        visibleColumns: allColumns,
        startColumnIndex: 0,
        endColumnIndex: allColumns.length - 1
      }
    }

    // If no scrollable columns, just return sticky (frozen + grouped)
    if (scrollableColumns.length === 0) {
      return {
        visibleColumns: stickyColumns,
        startColumnIndex: 0,
        endColumnIndex: stickyColumns.length - 1
      }
    }

    const viewportLeft = scrollLeft
    const viewportRight = scrollLeft + viewportWidth

    // Find first visible scrollable column
    let startIdx = 0
    for (let i = 0; i < scrollableColumns.length; i++) {
      const col = scrollableColumns[i]
      const colRight = col.left + col.column.width
      if (colRight > viewportLeft) {
        startIdx = Math.max(0, i - COLUMN_BUFFER)
        break
      }
      // If we've gone through all columns without finding a visible one,
      // start from the last few columns
      if (i === scrollableColumns.length - 1) {
        startIdx = Math.max(0, scrollableColumns.length - COLUMN_BUFFER)
      }
    }

    // Find last visible scrollable column
    let endIdx = scrollableColumns.length - 1
    for (let i = startIdx; i < scrollableColumns.length; i++) {
      const col = scrollableColumns[i]
      if (col.left > viewportRight) {
        endIdx = Math.min(scrollableColumns.length - 1, i + COLUMN_BUFFER - 1)
        break
      }
    }

    const visibleScrollable = scrollableColumns.slice(startIdx, endIdx + 1)

    return {
      visibleColumns: [...stickyColumns, ...visibleScrollable],
      startColumnIndex: stickyColumns.length + startIdx,
      endColumnIndex: stickyColumns.length + endIdx
    }
  }
  const { visibleColumns, startColumnIndex, endColumnIndex } = computeVisibleColumns()

  return {
    columns: visibleColumns,
    allColumns,
    lastFrozenColumnIndex,
    totalWidth,
    frozenWidth,
    startColumnIndex,
    endColumnIndex,
    treeColumnWidth,
    treeLeafColumn
  }
}
